import { consola } from 'consola'
import type { createClient } from 'redis'

let client: ReturnType<typeof createClient> | null = null
const cache: Record<string, string> = {}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic constraint
export const useCache = async <T extends Record<string, any>>(key: string, factory: () => T|Promise<T>) => {
  const timedFactory = async() => {
    const startTime = Date.now()
    const value = await factory()
    consola.debug(`Calling factory for "${key}" took ${Date.now() - startTime} milliseconds.`)
    return value
  }
  // Keep the json parse to prevent surprises when valkey is active on live instead of calling factory directly.
  const consistentFactory = async () => JSON.parse(JSON.stringify(await timedFactory()))

  // Only optionally enable local caching, as that might interfere with local development
  if (import.meta.server && process.env.CACHING_ENABLED === 'true') {
    if (process.env.VALKEY_URL) {
      const { context } = useRequestEvent()!
      if (context.cacheDisabled) {
        consola.debug(`Skipped fetching "${key}" from redis as cache has been disabled for the request.`)
        return await consistentFactory()
      } else {
        const startTime = Date.now()
        if (!client?.isReady) {
          const { createClient } = await import('redis')

          try {
            client = await createClient({
              url: process.env.VALKEY_URL,
              socket: {
                connectTimeout: 100,
              },
            }).connect()
          } catch (error) {
            context.cacheDisabled = true
            consola.error(new Error('error on establishing cache connection to redis', { cause: error }))
            return await consistentFactory()
          }
        }

        if (!client) {
          context.cacheDisabled = true
          return await consistentFactory()
        }

        let value: string|null = null
        try {
          value = await client.get(key)
        } catch (error) {
          context.cacheDisabled = true
          consola.error(new Error('error on fetching entry from redis', { cause: error}))
          return await consistentFactory()
        }
        if (value === null) {
          value = JSON.stringify(await timedFactory())
          try {
            await client.set(key, value)
          } catch (error) {
            consola.error(new Error('error on writing cache entry to redis', { cause: error }))
          }
        } else {
          consola.debug(`Fetched entry for "${key}" from redis in ${Date.now() - startTime} milliseconds.`)
        }
        return JSON.parse(value)
      }
    } else {
      if (!(key in cache)) {
        cache[key] = JSON.stringify(await timedFactory())
      } else {
        consola.debug(`Fetched entry for "${key}" from local cache.`)
      }
      return JSON.parse(cache[key])
    }
  } else {
    return consistentFactory()
  }
}
